import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Backdrop, Grow, Modal } from '@material-ui/core';
import moment from 'moment';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import Timezone from '../../commons/timezone/Timezone.js';
import * as Utils from '../../commons/utils/Utils';
import Dialog from '../../commons/dialog/Dialog.js';
import backendStrings from '../../lang/backendStrings.js';
import Loading from '../ui/Loading.js';
import CorrectivosAbm from '../correctivos/CorrectivosAbm';
import SolicitudAdjuntosLista from './SolicitudAdjuntosLista.jsx';
import { etiquetaLlavesEnVigilancia, llavesEnVigilanciaHabilitada } from './solicitudEstado.js';

const ESTILO_TEXTO_LARGO = {
	whiteSpace: 'pre-wrap',
	wordBreak: 'break-word',
	maxHeight: 250,
	overflowY: 'auto',
};

const SIN_UBICACION = { search: '', state: null };

const DatoSolicitud = ({ label, children }) => (
	<div className="form-group mb-1">
		<label className="label-control col-form-label pb-0">{label}:</label>
		<div className="form-value-detalle" style={ESTILO_TEXTO_LARGO}>{children}</div>
	</div>
);

const SolicitudLiberarModal = ({ solicitudId, onClose, onLiberada }) => {
	const intl = useIntl();
	const [solicitud, setSolicitud] = useState(null);

	useEffect(() => {
		setSolicitud(null);
		if (!solicitudId) return undefined;

		let vigente = true;
		Utils.getData('/solicitudes/' + solicitudId)
			.then((data) => {
				if (vigente) setSolicitud(data);
			})
			.catch(() => {
				if (vigente) onClose();
			});

		return () => {
			vigente = false;
		};
	}, [solicitudId]);

	const confirmarGenerado = (ticketId) =>
		Dialog.confirm(intl, {
			icon: 'success',
			title: intl.formatMessage({ id: 'solicitudLiberarModal.generado_titulo', defaultMessage: 'Correctivo #{ticket} generado' }, { ticket: ticketId }),
			text: intl.formatMessage(
				{ id: 'solicitudLiberarModal.generado_texto', defaultMessage: 'Generado desde la solicitud {numero}. Estado inicial ABIERTO.' },
				{ numero: solicitud.numero }
			),
			confirmText: intl.formatMessage({ id: 'solicitudLiberarModal.ver_correctivo', defaultMessage: 'Ver el correctivo' }),
			cancelText: intl.formatMessage({ id: 'solicitudLiberarModal.cerrar', defaultMessage: 'Cerrar' }),
		});

	const errorAlLiberar = (error) => {
		const detalle = error && error.response && error.response.data && error.response.data.detalle;
		const clave = Array.isArray(detalle) ? detalle[0] : null;
		return clave && backendStrings[clave] ? intl.formatMessage(backendStrings[clave]) : '';
	};

	const handleGuardado = (ticketId) => {
		onClose();
		Utils.sendData('/solicitudes/' + solicitud.id + '/liberar', 'PATCH', { ticket: ticketId })
			.then(() => confirmarGenerado(ticketId))
			.then((verTicket) => onLiberada(ticketId, verTicket))
			.catch((error) => {
				Dialog.alert(intl, {
					title: intl.formatMessage(
						{ id: 'solicitudLiberarModal.no_liberada_titulo', defaultMessage: 'El correctivo #{ticket} se generó, pero la solicitud {numero} no se pudo liberar' },
						{ ticket: ticketId, numero: solicitud.numero }
					),
					text: errorAlLiberar(error),
				});
				onLiberada(ticketId, false);
			});
	};

	if (!solicitudId) return null;
	if (!solicitud) return <Loading />;

	const entidad = solicitud.entidad || {};
	// VEC-4528: el activo se eligió al cargar la solicitud; el correctivo va sobre el asociado o, si no hay, sobre el principal.
	const movil = solicitud.activoAsociado || entidad;
	const marcaModelo = [movil.marca, movil.modelo].filter(Boolean).join(' ');
	const inmovilizadoHabilitado = ConfigBusiness.get('tickets.inmovilizado.habilitado') === 'true';

	return (
		<Modal open onClose={onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 300 }}>
			<Grow in>
				<div tabIndex="-1" role="dialog">
					<div className="modal-xl modal-dialog" role="document">
						<div className="modal-content">
							<div className="modal-header bg-fleet">
								<h4 className="modal-title text-white mb-0">
									<i className="icon-wrench align-middle icon-modal-title"></i>{' '}
									<FormattedMessage
										id="solicitudLiberarModal.titulo"
										defaultMessage="Generación de Mantenimiento Correctivo — desde la solicitud {numero}"
										values={{ numero: solicitud.numero }}
									/>
								</h4>
								<button type="button" className="close" aria-label="Close" onClick={onClose}>
									<span aria-hidden="true">&times;</span>
								</button>
							</div>
							<div className="modal-body modal-grey modal-mh ovf-x-hidden">
								<div className="row">
									<div className="col-12 col-lg-4">
										<div className="card pull-up">
											<div className="card-content">
												<div className="card-body">
													<h4 className="form-section">
														<i className="la la-file-text-o"></i>
														<FormattedMessage id="solicitudLiberarModal.datos_solicitud" defaultMessage=" Datos que trae la solicitud" />
													</h4>
													<DatoSolicitud label={intl.formatMessage({ id: 'solicitudLiberarModal.movil', defaultMessage: 'Móvil' })}>
														{marcaModelo ? movil.dominio + ' — ' + marcaModelo : movil.dominio}
													</DatoSolicitud>
													<DatoSolicitud label={intl.formatMessage({ id: 'solicitudLiberarModal.unidad', defaultMessage: 'Unidad' })}>
														{movil.unidad || ''}
													</DatoSolicitud>
													{movil.id !== entidad.id && (
														<DatoSolicitud label={intl.formatMessage({ id: 'solicitudLiberarModal.activo_vinculado_de', defaultMessage: 'Activo vinculado de' })}>
															{entidad.dominio}
														</DatoSolicitud>
													)}
													{movil.id !== entidad.id && (
														<DatoSolicitud label={intl.formatMessage({ id: 'solicitudLiberarModal.unidad_principal', defaultMessage: 'Unidad del móvil principal' })}>
															{entidad.unidad || ''}
														</DatoSolicitud>
													)}
													<DatoSolicitud label={intl.formatMessage({ id: 'solicitudLiberarModal.solicitante', defaultMessage: 'Solicitante' })}>
														{solicitud.solicitante + ' — ' + Timezone.getDateForClient(solicitud.fechaHora, null, 'DD/MM/YYYY HH:mm')}
													</DatoSolicitud>
													<DatoSolicitud label={intl.formatMessage({ id: 'solicitudLiberarModal.base', defaultMessage: 'Base' })}>
														{solicitud.base || ''}
													</DatoSolicitud>
													{llavesEnVigilanciaHabilitada() && (
														<DatoSolicitud label={intl.formatMessage({ id: 'solicitudes.llaves_en_vigilancia', defaultMessage: 'Llaves en vigilancia' })}>
															{etiquetaLlavesEnVigilancia(intl, solicitud.llavesEnVigilancia)}
														</DatoSolicitud>
													)}
													{inmovilizadoHabilitado && solicitud.inmovilizado !== null && (
														<DatoSolicitud label={intl.formatMessage({ id: 'correctivosAbm.datos_generales.inmovilizado', defaultMessage: 'Inmovilizado' })}>
															{solicitud.inmovilizado ? (
																<span className="text-danger">{intl.formatMessage({ id: 'correctivosAbm.inmovilizadoOptions.si', defaultMessage: 'SI' })}</span>
															) : (
																intl.formatMessage({ id: 'correctivosAbm.inmovilizadoOptions.no', defaultMessage: 'NO' })
															)}
														</DatoSolicitud>
													)}
													{solicitud.fechaHoraTurno && (
														<DatoSolicitud label={intl.formatMessage({ id: 'solicitudLiberarModal.fecha_turno', defaultMessage: 'Fecha de Turno' })}>
															{moment(solicitud.fechaHoraTurno).format('DD/MM/YYYY HH:mm')}
														</DatoSolicitud>
													)}
													<DatoSolicitud label={intl.formatMessage({ id: 'solicitudLiberarModal.detalle', defaultMessage: 'Detalle' })}>
														{solicitud.detalle}
													</DatoSolicitud>
													<label className="label-control col-form-label pb-0">
														<FormattedMessage id="solicitudLiberarModal.adjuntos" defaultMessage="Adjuntos ({cantidad}):" values={{ cantidad: solicitud.adjuntos.length }} />
													</label>
													{solicitud.adjuntos.length > 0 ? (
														<SolicitudAdjuntosLista adjuntos={solicitud.adjuntos} />
													) : (
														<div className="text-muted">
															<FormattedMessage id="solicitudAdjuntosModal.vacio" defaultMessage="La solicitud no tiene adjuntos." />
														</div>
													)}
													<div className="alert alert-info mt-1 mb-0" role="alert">
														<i className="la la-info-circle"></i>{' '}
														<FormattedMessage id="solicitudLiberarModal.nota_precarga" defaultMessage="Datos precargados desde la solicitud (solo lectura)." />
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className="col-12 col-lg-8">
										<CorrectivosAbm
											action="ADD"
											option="MOVIL"
											location={SIN_UBICACION}
											match={{ params: { movilId: movil.id, movilDominio: movil.dominio } }}
											solicitud={solicitud}
											onCancelarDesdeSolicitud={onClose}
											onTicketCreadoDesdeSolicitud={handleGuardado}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Grow>
		</Modal>
	);
};

export default SolicitudLiberarModal;
